(() => {
  'use strict';


  /* =======================================================
     REDUCED MOTION
     ======================================================= */

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;


  /* =======================================================
     GLOBAL MOTION STATE
     ======================================================= */

  if (!reduceMotion) {
    document.documentElement.classList.add(
      'glaia-global-motion'
    );
  }


  /* =======================================================
     PROTECT DRAWERS / MODALS FROM LENIS
     ======================================================= */

  const markIndependentScrollAreas = () => {

    const selectors = [
      '[role="dialog"]',
      '[aria-modal="true"]',
      '.cart-drawer',
      '.drawer',
      '.menu-drawer',
      '.predictive-search',
      '.modal',
      '.popup',
      '[class*="cart-drawer"]',
      '[class*="menu-drawer"]'
    ];


    document
      .querySelectorAll(
        selectors.join(',')
      )
      .forEach((element) => {

        element.setAttribute(
          'data-lenis-prevent',
          ''
        );

      });

  };


  /* =======================================================
     LENIS
     ======================================================= */

  let lenis = null;


  if (
    !reduceMotion &&
    typeof window.Lenis !== 'undefined'
  ) {

    lenis = new window.Lenis({

      autoRaf: true,

      lerp: 0.10,

      smoothWheel: true,

      /*
        Keep touch scrolling native on mobile.
      */
      syncTouch: false,

      wheelMultiplier: 0.9,

      touchMultiplier: 1,

      anchors: true

    });


    window.glaiaGlobalLenis = lenis;

  }


  /* =======================================================
     COLLECTION CARD OBSERVER
     ======================================================= */

  let collectionObserver = null;


  const createCollectionObserver = () => {

    if (
      reduceMotion ||
      !('IntersectionObserver' in window)
    ) {
      return null;
    }


    return new IntersectionObserver(
      (entries, observer) => {

        entries.forEach((entry) => {

          if (!entry.isIntersecting) {
            return;
          }


          entry.target.classList.add(
            'is-visible'
          );


          observer.unobserve(
            entry.target
          );

        });

      },
      {
        threshold: 0.08,

        rootMargin:
          '0px 0px -4% 0px'
      }
    );

  };


  collectionObserver =
    createCollectionObserver();


  /* =======================================================
     COLLECTION MOTION
     ======================================================= */

  const initCollectionMotion = () => {

    const cards =
      document.querySelectorAll(
        '.product-grid__item:not([data-glaia-card-ready])'
      );


    if (!cards.length) {
      return;
    }


    cards.forEach(
      (card, index) => {

        card.dataset.glaiaCardReady =
          'true';


        card.classList.add(
          'glaia-collection-card'
        );


        /*
          Short stagger that resets instead of
          progressively delaying every card.
        */

        const delay =
          (index % 4) * 55;


        card.style.setProperty(
          '--glaia-card-delay',
          `${delay}ms`
        );


        if (
          reduceMotion ||
          !collectionObserver
        ) {

          card.classList.add(
            'is-visible'
          );

          return;

        }


        collectionObserver.observe(
          card
        );

      }
    );

  };


  /* =======================================================
     PRODUCT PAGE MOTION
     ======================================================= */

  const initProductMotion = () => {

    const sections =
      document.querySelectorAll(
        '[data-testid="product-information"]:not([data-glaia-product-ready])'
      );


    if (!sections.length) {
      return;
    }


    sections.forEach((section) => {

      /*
        Do not animate product information inside
        Quick Add / modal interfaces.
      */

      if (
        section.closest(
          'dialog, [aria-modal="true"], .quick-add-modal'
        )
      ) {
        return;
      }


      section.dataset.glaiaProductReady =
        'true';


      section.classList.add(
        'glaia-product-motion'
      );


      const productDetails =
        section.querySelector(
          '.product-details'
        );


      if (productDetails) {

        const group =
          productDetails.querySelector(
            ':scope > .group-block'
          );


        const detailItems =
          group
            ? Array.from(group.children)
            : Array.from(productDetails.children);


        detailItems.forEach(
          (item, index) => {

            item.classList.add(
              'glaia-product-detail-item'
            );


            /*
              Stagger product information.

              Delay is capped so longer product pages
              never become slow or theatrical.
            */

            const delay =
              120 +
              Math.min(index, 8) * 65;


            item.style.setProperty(
              '--glaia-product-detail-delay',
              `${delay}ms`
            );

          }
        );

      }


      /*
        Reduced-motion users see everything immediately.
      */

      if (reduceMotion) {

        section.classList.add(
          'is-visible'
        );

        return;

      }


      /*
        Two RAF calls allow the initial CSS state
        to paint before transitioning.
      */

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          section.classList.add(
            'is-visible'
          );

        });

      });

    });

  };


  /* =======================================================
     REFRESH
     ======================================================= */

  let refreshQueued = false;


  const refresh = () => {

    markIndependentScrollAreas();

    initCollectionMotion();

    initProductMotion();


    if (lenis) {
      lenis.resize();
    }

  };


  const queueRefresh = () => {

    if (refreshQueued) {
      return;
    }


    refreshQueued = true;


    requestAnimationFrame(() => {

      refreshQueued = false;

      refresh();

    });

  };


  /* =======================================================
     COLLECTION DOM CHANGES

     Watch only the collection results instead of the
     entire document. This keeps Shop performance lighter.
     ======================================================= */

  const initCollectionMutationObserver = () => {

    const resultsList =
      document.querySelector(
        'results-list'
      );


    if (!resultsList) {
      return;
    }


    const mutationObserver =
      new MutationObserver(
        queueRefresh
      );


    mutationObserver.observe(
      resultsList,
      {
        childList: true,
        subtree: true
      }
    );

  };


  /* =======================================================
     INITIALIZE
     ======================================================= */

  const init = () => {

    refresh();

    initCollectionMutationObserver();

  };


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();

  }


  /* =======================================================
     SHOPIFY THEME EDITOR
     ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    queueRefresh
  );


  document.addEventListener(
    'shopify:section:reorder',
    queueRefresh
  );


  /* =======================================================
     RESIZE
     ======================================================= */

  window.addEventListener(
    'resize',
    () => {

      if (lenis) {
        lenis.resize();
      }

    },
    {
      passive: true
    }
  );

})();